// Imports
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/Card';
import { Tabs, TabsList, TabsTrigger } from './components/ui/Tabs';
import { Input } from './components/ui/Input';
import { Label } from './components/ui/Label';
import { Switch } from './components/ui/Switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/Select';

// Helper functions
const formatNumber = (value) => {
  if (!value && value !== 0) return '';
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const parseFormattedNumber = (value) => {
  if (!value) return 0;
  return Number(value.toString().replace(/,/g, ''));
};

const calculatePMI = (loanAmount, homePrice) => {
  const ltv = (loanAmount / homePrice) * 100;
  return ltv > 80 ? (loanAmount * 0.007) / 12 : 0;
};

const calculateInsurance = (homePrice) => {
  if (homePrice <= 500000) {
    return 1200;
  } else if (homePrice <= 1000000) {
    return 1800;
  } else if (homePrice <= 1500000) {
    return 2400;
  } else {
    return 3000;
  }
};

// Main Component
const MortgageCalculator = () => {
  // State definitions
  const [activeTab, setActiveTab] = useState('monthly');
  const [showTaxInputs, setShowTaxInputs] = useState(false);
  
  const [monthlyFormData, setMonthlyFormData] = useState({
    homePrice: 300000,
    downPayment: 60000,
    downPaymentPercentage: 20,
    loanTerm: '30-yr fixed',
    interestRate: 7.445,
    propertyTax: 3750,
    homeInsurance: 1800,
    homeownersAssociation: 0
  });

  const [isPropertyTaxOverridden, setIsPropertyTaxOverridden] = useState(false);

  const [purchaseFormData, setPurchaseFormData] = useState({
    yearlyIncome: 57000,
    monthlyDebts: 250,
    downPayment: 60000,
    loanTerm: '30-yr fixed',
    interestRate: 7.445,
    propertyTax: 3750,
    homeInsurance: 1800,
    homeownersAssociation: 0
  });

  const [monthlyResults, setMonthlyResults] = useState({
    monthlyPayment: 0,
    loanPayment: 0,
    taxesAndFees: 0,
    pmi: 0
  });

  const [purchaseResults, setPurchaseResults] = useState({
    purchaseBudget: 0,
    mortgageAmount: 0,
    monthlyPayment: 0,
    actualMonthlyPayment: 0,
    isLowDownPayment: false,
    downPaymentPercentage: 0
  });

  // Calculation functions
  const calculateMonthlyPayment = () => {
    const principal = monthlyFormData.homePrice - monthlyFormData.downPayment;
    const rate = monthlyFormData.interestRate / 100 / 12;
    const numberOfPayments = 360;
    
    const loanPayment = 
      (principal * rate * Math.pow(1 + rate, numberOfPayments)) /
      (Math.pow(1 + rate, numberOfPayments) - 1);

    const pmi = calculatePMI(principal, monthlyFormData.homePrice);
    
    const taxesAndFees = 
      (monthlyFormData.propertyTax / 12) +
      (monthlyFormData.homeInsurance / 12) +
      monthlyFormData.homeownersAssociation;
    
    setMonthlyResults({
      loanPayment: Math.round(loanPayment),
      taxesAndFees: Math.round(taxesAndFees),
      pmi: Math.round(pmi),
      monthlyPayment: Math.round(loanPayment + taxesAndFees + pmi)
    });
  };

  const calculatePurchaseBudget = () => {
    const monthlyIncome = purchaseFormData.yearlyIncome / 12;
    const maxMonthlyPayment = Math.round((monthlyIncome * 0.48) - purchaseFormData.monthlyDebts);
    
    let currentLoan = maxMonthlyPayment * 200;
    let found = false;
    let step = currentLoan / 2;
    let iterations = 0;
    const maxIterations = 20;
    
    while (!found && iterations < maxIterations) {
      const rate = purchaseFormData.interestRate / 100 / 12;
      const numberOfPayments = 360;
      
      const piPayment = 
        (currentLoan * rate * Math.pow(1 + rate, numberOfPayments)) /
        (Math.pow(1 + rate, numberOfPayments) - 1);
      
      // Use existing property tax if overridden, otherwise calculate
      const propertyTax = isPropertyTaxOverridden 
        ? purchaseFormData.propertyTax / 12
        : (currentLoan + purchaseFormData.downPayment) * 0.0125 / 12;
      
      const homeInsurance = purchaseFormData.homeInsurance / 12;
      const hoa = purchaseFormData.homeownersAssociation;
      
      const ltv = (currentLoan / (currentLoan + purchaseFormData.downPayment)) * 100;
      const pmi = ltv > 80 ? (currentLoan * 0.007) / 12 : 0;
      
      const totalPayment = piPayment + propertyTax + homeInsurance + hoa + pmi;
      
      if (Math.abs(totalPayment - maxMonthlyPayment) < 1 || step < 1) {
        found = true;
      } else if (totalPayment > maxMonthlyPayment) {
        currentLoan -= step;
      } else {
        currentLoan += step;
      }
      
      step /= 2;
      iterations++;
    }
    
    const totalBudget = Math.round(currentLoan + purchaseFormData.downPayment);
    
    // Only update property tax if it's not overridden
    if (!isPropertyTaxOverridden) {
      const newPropertyTax = Math.round(totalBudget * 0.0125);
      setPurchaseFormData(prev => ({
        ...prev,
        propertyTax: newPropertyTax
      }));
    }
    
    const rate = purchaseFormData.interestRate / 100 / 12;
    const numberOfPayments = 360;
    const piPayment = 
      (currentLoan * rate * Math.pow(1 + rate, numberOfPayments)) /
      (Math.pow(1 + rate, numberOfPayments) - 1);
    
    const propertyTax = isPropertyTaxOverridden
      ? purchaseFormData.propertyTax / 12
      : totalBudget * 0.0125 / 12;
    
    const homeInsurance = purchaseFormData.homeInsurance / 12;
    const hoa = purchaseFormData.homeownersAssociation;
    const ltv = (currentLoan / totalBudget) * 100;
    const pmi = ltv > 80 ? (currentLoan * 0.007) / 12 : 0;
    
    const actualMonthlyPayment = Math.round(piPayment + propertyTax + homeInsurance + hoa + pmi);
    const downPaymentPercentage = (purchaseFormData.downPayment / totalBudget) * 100;

    setPurchaseResults({
      purchaseBudget: totalBudget,
      mortgageAmount: Math.round(currentLoan),
      monthlyPayment: Math.round(maxMonthlyPayment),
      actualMonthlyPayment: actualMonthlyPayment,
      isLowDownPayment: downPaymentPercentage < 5,
      downPaymentPercentage: Math.round(downPaymentPercentage)
    });
  };

  // Effect hooks
  useEffect(() => {
    if (activeTab === 'monthly') {
      calculateMonthlyPayment();
    }
  }, [monthlyFormData, activeTab]);

  useEffect(() => {
    if (activeTab === 'purchase') {
      calculatePurchaseBudget();
    }
  }, [purchaseFormData, activeTab]);

  // Event handlers
  const handleMonthlyInputChange = (field, value) => {
    const numValue = field === 'interestRate' ? value : parseFormattedNumber(value);
    
    if (field === 'downPayment') {
      const percentage = (numValue / monthlyFormData.homePrice) * 100;
      setMonthlyFormData(prev => ({
        ...prev,
        [field]: numValue,
        downPaymentPercentage: Math.round(percentage)
      }));
    } else if (field === 'downPaymentPercentage') {
      const amount = (monthlyFormData.homePrice * numValue) / 100;
      setMonthlyFormData(prev => ({
        ...prev,
        downPayment: Math.round(amount),
        [field]: numValue
      }));
    } else if (field === 'homePrice') {
      const newDownPayment = (monthlyFormData.downPaymentPercentage * numValue) / 100;
      const newPropertyTax = Math.round(numValue * 0.0125);
      const newInsurance = calculateInsurance(numValue);
      setMonthlyFormData(prev => ({
        ...prev,
        [field]: numValue,
        downPayment: Math.round(newDownPayment),
        propertyTax: newPropertyTax,
        homeInsurance: newInsurance
      }));
    } else {
      setMonthlyFormData(prev => ({
        ...prev,
        [field]: numValue
      }));
    }
  };
  
  const handlePurchaseInputChange = (field, value) => {
    const numValue = field === 'interestRate' ? value : parseFormattedNumber(value);
    
    // Set the override flag when property tax is manually changed
    if (field === 'propertyTax') {
      setIsPropertyTaxOverridden(true);
    }
    
    setPurchaseFormData(prev => ({
      ...prev,
      [field]: numValue 
    }));
  };


  // Render
  return (  
    <div className="w-full max-w-7xl mx-auto p-4">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-8">
          <Card className="h-full bg-white border-gray-200">
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="text-2xl font-light text-gray-900"></CardTitle>
            </CardHeader>
            
            <CardContent className="space-y-6 pt-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg mb-6">
                  <TabsTrigger 
                    value="monthly" 
                    className={`rounded-md py-2 px-4 text-sm font-medium transition-colors
                      ${activeTab === 'monthly' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    Monthly Payment
                  </TabsTrigger>
                  <TabsTrigger 
                    value="purchase"
                    className={`rounded-md py-2 px-4 text-sm font-medium transition-colors
                      ${activeTab === 'purchase' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    Purchase Budget
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={showTaxInputs}
                  onCheckedChange={setShowTaxInputs}
                  id="taxes-toggle"
                  className="data-[state=checked]:bg-gray-900"
                />
                <Label htmlFor="taxes-toggle" className="text-sm text-gray-600">Show taxes & fees inputs</Label>
              </div>

              {activeTab === 'monthly' ? (
                <div className="space-y-6">
                  <div>
                    <Label className="text-sm text-gray-600">Home price</Label>
                    <Input
                      type="text"
                      value={formatNumber(monthlyFormData.homePrice)}
                      onChange={(e) => handleMonthlyInputChange('homePrice', e.target.value.replace(/[^\d]/g, ''))}
                      className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-600">Down payment</Label>
                      <Input
                        type="text"
                        value={formatNumber(monthlyFormData.downPayment)}
                        onChange={(e) => handleMonthlyInputChange('downPayment', e.target.value.replace(/[^\d]/g, ''))}
                        className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Down payment %</Label>
                      <Input
                        type="text"
                        value={monthlyFormData.downPaymentPercentage}
                        onChange={(e) => handleMonthlyInputChange('downPaymentPercentage', e.target.value.replace(/[^\d]/g, ''))}
                        className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-600">Interest rate</Label>
                      <Input
                        type="text"
                        value={monthlyFormData.interestRate}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            handleMonthlyInputChange('interestRate', value);
                          }
                        }}
                        className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Loan term</Label>
                      <Input
                        type="text"
                        value="30-yr fixed"
                        readOnly
                        className="mt-1 h-10 border-gray-200 text-gray-900 cursor-default"
                      />
                    </div>
                  </div>

                  {showTaxInputs && (
                    <div className="space-y-4 border-t border-gray-200 pt-4">
                      <div>
                        <Label className="text-sm text-gray-600">Property tax (yearly)</Label>
                        <Input
                          type="text"
                          value={formatNumber(monthlyFormData.propertyTax)}
                          onChange={(e) => handleMonthlyInputChange('propertyTax', e.target.value.replace(/[^\d]/g, ''))}
                          className="mt-1 h-10 border-gray-200 text-gray-900focus:border-gray-900"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Home insurance (yearly)</Label>
                        <Input
                          type="text"
                          value={formatNumber(monthlyFormData.homeInsurance)}
                          onChange={(e) => handleMonthlyInputChange('homeInsurance', e.target.value.replace(/[^\d]/g, ''))}
                          className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">HOA (monthly)</Label>
                        <Input
                          type="text"
                          value={formatNumber(monthlyFormData.homeownersAssociation)}
                          onChange={(e) => handleMonthlyInputChange('homeownersAssociation', e.target.value.replace(/[^\d]/g, ''))}
                          className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <Label className="text-sm text-gray-600">Yearly income</Label>
                    <Input
                      type="text"
                      value={formatNumber(purchaseFormData.yearlyIncome)}
                      onChange={(e) => handlePurchaseInputChange('yearlyIncome', e.target.value.replace(/[^\d]/g, ''))}
                      className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Monthly debts</Label>
                    <Input
                      type="text"
                      value={formatNumber(purchaseFormData.monthlyDebts)}
                      onChange={(e) => handlePurchaseInputChange('monthlyDebts', e.target.value.replace(/[^\d]/g, ''))}
                      className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Down payment available</Label>
                    <Input
                      type="text"
                      value={formatNumber(purchaseFormData.downPayment)}
                      onChange={(e) => handlePurchaseInputChange('downPayment', e.target.value.replace(/[^\d]/g, ''))}
                      className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-gray-600">Interest rate</Label>
                      <Input
                        type="text"
                        value={purchaseFormData.interestRate}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            handlePurchaseInputChange('interestRate', value);
                          }
                        }}
                        className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Loan term</Label>
                      <Input
                        type="text"
                        value="30-yr fixed"
                        readOnly
                        className="mt-1 h-10 border-gray-200 text-gray-900 cursor-default"
                      />
                    </div>
                  </div>

                  {showTaxInputs && (
                    <div className="space-y-4 border-t border-gray-200 pt-4">
                      <div>
                        <Label className="text-sm text-gray-600">Property tax (yearly)</Label>
                        <Input
                          type="text"
                          value={formatNumber(purchaseFormData.propertyTax)}
                          onChange={(e) => handlePurchaseInputChange('propertyTax', e.target.value.replace(/[^\d]/g, ''))}
                          className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Home insurance (yearly)</Label>
                        <Input
                          type="text"
                          value={formatNumber(purchaseFormData.homeInsurance)}
                          onChange={(e) => handlePurchaseInputChange('homeInsurance', e.target.value.replace(/[^\d]/g, ''))}
                          className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">HOA (monthly)</Label>
                        <Input
                          type="text"
                          value={formatNumber(purchaseFormData.homeownersAssociation)}
                          onChange={(e) => handlePurchaseInputChange('homeownersAssociation', e.target.value.replace(/[^\d]/g, ''))}
                          className="mt-1 h-10 border-gray-200 text-gray-900 focus:border-gray-900"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Results Column */}
        <div className="md:col-span-4">
          <Card className="h-full bg-white border-gray-200">
            <CardContent className="pt-6">
              {activeTab === 'monthly' ? (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg text-gray-600">Total monthly payment</h2>
                    <div className="text-4xl font-light text-gray-900">${formatNumber(monthlyResults.monthlyPayment)}</div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="text-gray-600">Principal & Interest</div>
                      <div className="text-2xl font-light text-gray-900">${formatNumber(monthlyResults.loanPayment)}</div>
                    </div>
                    {monthlyResults.pmi > 0 && (
                      <div>
                        <div className="text-gray-600">Private mortgage insurance</div>
                        <div className="text-2xl font-light text-gray-900">${formatNumber(monthlyResults.pmi)}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-gray-600">Taxes, Insurance, HOA</div>
                      <div className="text-2xl font-light text-gray-900">${formatNumber(monthlyResults.taxesAndFees)}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg text-gray-600">Maximum purchase budget</h2>
                    <div className="text-4xl font-light text-gray-900">${formatNumber(purchaseResults.purchaseBudget)}</div>
                    <div className="mt-2 text-sm text-gray-500">
                      Including ${formatNumber(purchaseFormData.downPayment)} down payment
                    </div>
                    {purchaseResults.isLowDownPayment && (
                      <div className="mt-2 text-sm text-red-600 font-medium">
                        Warning: Down payment is under 5% ({purchaseResults.downPaymentPercentage}%)
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-gray-600">Monthly payment</div>
                    <div className="text-2xl font-light text-gray-900">
                      ${formatNumber(purchaseResults.actualMonthlyPayment)}
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                      Maximum allowed: ${formatNumber(purchaseResults.monthlyPayment)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-600">Mortgage amount</div>
                    <div className="text-2xl font-light text-gray-900">
                      ${formatNumber(purchaseResults.mortgageAmount)}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MortgageCalculator;